import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { formatDateTimeRange } from '../../../utils/Date.Util';
import { ColorType, Divider, Label, LabelType } from '../../_ui';

export const SparkResultViewerV3 = () => {
  const { t } = useTranslation();

  const sparkResultData = sessionStorage.getItem('sparkResultDataV3');
  const parsedSparkData = sparkResultData ? JSON.parse(sparkResultData) : null;

  const charger = sessionStorage.getItem('charger');
  const parsedCharger = charger ? JSON.parse(charger) : null;

  const dateTimeRange = sessionStorage.getItem('dateTimeRange');
  const parsedDateTimeRange = dateTimeRange ? JSON.parse(dateTimeRange) : null;

  const renderErrorDetected = (data: any) => {
    const { type, content } = data;
    return content.map((item: any) => (
      <div key={uniqueId()}>
        <div className='flex flex-col gap-2'>
          <div className='flex flex-row items-center gap-1'>
            <Label type={LabelType.LABEL_M} text={`${type}: `} />
            <Label type={LabelType.BODY3} text={item.signalName} />
          </div>
          {item.severity && item.events && (
            <div className='flex flex-row items-center gap-4'>
              <div className='flex flex-row items-center gap-1'>
                <Label type={LabelType.LABEL_S_MEDIUM} text={`${t('spark_result_severity')}:`} />
                <Label type={LabelType.BODY3} text={item.severity} />
              </div>
              <div className='flex flex-row items-center gap-1'>
                <Label type={LabelType.LABEL_S_MEDIUM} text={`${t('spark_result_events')}:`} />
                <Label type={LabelType.BODY3} text={item.events} />
              </div>
            </div>
          )}
          {item.possible_causes?.length > 0 && (
            <div className='flex flex-col gap-1'>
              <Label
                type={LabelType.LABEL_S_MEDIUM}
                text={`${t('spark_result_possible_cause')}:`}
              />
              {item.possible_causes.map((cause: string, causeIndex: number) => (
                <div key={cause} className='flex flex-row gap-2'>
                  <Label
                    type={LabelType.BODY3}
                    text={`${causeIndex + 1}. `}
                    color={ColorType.GREY6}
                  />
                  <Label type={LabelType.BODY3} text={cause} color={ColorType.GREY6} />
                </div>
              ))}
            </div>
          )}
          {item.how_to_troubleshoot?.length > 0 && (
            <div className='flex flex-col gap-1'>
              <Label
                type={LabelType.LABEL_S_MEDIUM}
                text={`${t('spark_result_how_to_troubleshoot')}: `}
              />
              {item.how_to_troubleshoot.map((step: string, tsIndex: number) => (
                <div key={step} className='flex flex-row gap-2'>
                  <Label type={LabelType.BODY3} text={`${tsIndex + 1}. `} color={ColorType.GREY6} />
                  <Label type={LabelType.BODY3} text={step} color={ColorType.GREY6} />
                </div>
              ))}
            </div>
          )}
          {item.name && (
            <div className='flex flex-col gap-1'>
              <div className='flex flex-row items-center gap-1'>
                <Label type={LabelType.LABEL_M} text={`${t('spark_result_name')}:`} />
                <Label type={LabelType.BODY3} text={item.name} />
              </div>
            </div>
          )}
          {item.definition && (
            <div className='flex flex-col gap-1'>
              <Label type={LabelType.LABEL_S_MEDIUM} text={`${t('spark_result_definition')}:`} />
              <Label type={LabelType.BODY3} text={item.definition} />
            </div>
          )}
        </div>

        <Divider className='my-4' />
      </div>
    ));
  };

  const renderSignalGlossary = (data: any) => {
    const { type, content } = data;
    return (
      <div key={uniqueId()} className='flex flex-col gap-2'>
        <Label type={LabelType.LABEL_M} text={`${type}:`} />
        {content.map((item: any) => (
          <div key={uniqueId()} className='flex flex-col gap-2'>
            {item.name && item.definition && (
              <div className='flex flex-col gap-1'>
                <div className='flex flex-col gap-1'>
                  <Label type={LabelType.LABEL_S_MEDIUM} text={`${item.name}:`} />
                  <Label type={LabelType.BODY3} text={item.definition} />
                </div>
              </div>
            )}
          </div>
        ))}
        <Divider className='my-4' />
      </div>
    );
  };

  const renderSignalSeverityDefinitions = (data: any) => {
    const { type, content } = data;
    return (
      <div key={uniqueId()} className='flex flex-col gap-2'>
        <Label type={LabelType.LABEL_M} text={`${type}:`} />
        {content.map((item: any) => (
          <div key={uniqueId()} className='flex flex-col gap-2'>
            {item.name && item.definition && (
              <div className='flex flex-col gap-1'>
                <div className='flex flex-col gap-1'>
                  <Label type={LabelType.LABEL_S_MEDIUM} text={`${item.name}:`} />
                  <Label type={LabelType.BODY3} text={item.definition} />
                </div>
              </div>
            )}
          </div>
        ))}
        <Divider className='my-4' />
      </div>
    );
  };

  const renderSummary = (data: any) => {
    const { type, content } = data;
    return (
      <div key={uniqueId()} className='flex flex-col gap-2'>
        <Label type={LabelType.LABEL_M} text={`${type}:`} />
        <Label type={LabelType.BODY3} text={content.length > 0 ? content[0].text : ''} />
      </div>
    );
  };

  const renderResultResult = () => {
    if (!parsedSparkData?.length) {
      return null;
    }
    return (
      <div className='flex flex-col gap-4'>
        {parsedSparkData.map((data: any) => {
          switch (data.type.toUpperCase()) {
            case 'ERROR DETECTED':
              return renderErrorDetected(data);
            case 'SIGNAL GLOSSARY':
              return renderSignalGlossary(data);
            case 'SIGNAL SEVERITY DEFINITIONS':
              return renderSignalSeverityDefinitions(data);
            case 'SUMMARY':
              return renderSummary(data);
            default:
              return null;
          }
        })}
      </div>
    );
  };

  if (!parsedSparkData) {
    return <div>{t('spark_results_not_found')}</div>;
  }

  return (
    <div className='flex flex-col gap-4 pb-4 m-5' id='print-container'>
      <div className='flex flex-row gap-8'>
        <Label text={t('charger_id_label')} type={LabelType.LABEL_S} className='min-w-[80px]' />
        <Label text={parsedCharger.name} type={LabelType.BODY3} />
      </div>
      <div className='flex flex-row gap-8'>
        <Label
          text={t('spark_time_range_label')}
          type={LabelType.LABEL_S}
          className='min-w-[80px]'
        />
        <Label
          text={formatDateTimeRange([
            new Date(parsedDateTimeRange.selDates[0]),
            new Date(parsedDateTimeRange.selDates[1]),
          ])}
          type={LabelType.BODY3}
        />
      </div>
      <Divider />
      {renderResultResult()}
    </div>
  );
};
